import { Box, Container, Typography, TextField, Button, Paper, Alert, Snackbar, FormControl, InputLabel, Select, MenuItem, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { sendEnquiryEmail } from '../../utils/emailService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { displayTitleSx } from '../../styles/typography';

const enquiryTypes = [
  { value: 'general', label: 'General Enquiry' },
  { value: 'builder', label: 'Builder Enquiry' },
  { value: 'installer', label: 'Installer/Contractor Enquiry' },
  { value: 'customer', label: 'Customer Support Enquiry' },
];

const Enquiry = () => {
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    enquiryType: 'general' // default value
  });
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Check if reCAPTCHA key is available
  useEffect(() => {
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      console.error("reCAPTCHA site key is missing");
      setRecaptchaError("reCAPTCHA configuration error. Please contact support.");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // For testing - log the current state
    console.log('Submitting form:', { 
      formData, 
      captchaValue, 
      isCaptchaResolved: !!captchaValue
    });
    
    setIsSubmitting(true);
    
    // Allow form submission without reCAPTCHA in development
    if (!captchaValue && import.meta.env.DEV) {
      console.warn('Bypassing reCAPTCHA in development mode');
    } else if (!captchaValue) {
      setSnackbar({
        open: true,
        message: 'Please complete the reCAPTCHA verification',
        severity: 'error'
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Send email using the service
      const result = await sendEnquiryEmail(formData);
      console.log('Email result:', result);
      
      if (result?.success) {
        // Store reference number for display
        setReferenceNumber(result.referenceNumber);
        // Set submitted state to show success message
        setSubmitted(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          enquiryType: 'general'
        });
        setCaptchaValue(null);
      } else {
        throw new Error('Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while submitting the form. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleCaptchaChange = (value: string | null) => {
    console.log('reCAPTCHA value:', value);
    setCaptchaValue(value);
    if (value) {
      setRecaptchaError(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStartOver = () => {
    setSubmitted(false);
    setReferenceNumber(null);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#f8fbff',
        py: { xs: 6, md: 10 },
        position: 'relative'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            maxWidth: 800, 
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4 }
          }}>
            <Typography 
              variant="h1" 
              align="center"
              sx={{
                ...displayTitleSx,
                color: '#0066b2',
                fontWeight: 700,
                mb: { xs: 3, md: 4 },
              }}
            >
              Contact Us
            </Typography>

            {/* Point existing customers at the support page before the form */}
            {!submitted && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  mb: { xs: 3, md: 4 },
                  borderRadius: 2,
                  bgcolor: 'rgba(0, 102, 178, 0.06)',
                  borderLeft: '4px solid #0066b2',
                }}
              >
                <Typography
                  sx={{
                    color: '#334155',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.8,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600, color: '#0066b2' }}>
                    Need help with an existing system?
                  </Box>{' '}
                  If your ducted heating, evaporative cooling, split system or panel heater
                  isn't working the way it should, our{' '}
                  <Link
                    component={RouterLink}
                    to="/support"
                    sx={{ color: '#0066b2', fontWeight: 600, textDecorationColor: 'rgba(0, 102, 178, 0.4)' }}
                  >
                    Support page
                  </Link>{' '}
                  is the fastest way to get it fixed. You can work through the common
                  faults there and log a support request that goes straight to our service
                  team. Use the form below for new enquiries, quotes, builder and installer
                  questions, or anything else.
                </Typography>
              </Paper>
            )}

            {/* Success view */}
            {submitted ? (
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f9fffc 0%, #e6f7ff 100%)',
                  borderRadius: { xs: '16px', sm: '24px' },
                  mb: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                  }
                }}
              >
                <CheckCircleOutlineIcon 
                  color="success" 
                  sx={{ 
                    fontSize: { xs: 60, sm: 80 },
                    mb: 2,
                    opacity: 0.9
                  }} 
                />
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                    mb: { xs: 1, sm: 2 },
                    fontWeight: 600,
                    color: '#2E7D32',
                  }}
                >
                  Thank You!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    maxWidth: '700px',
                    mx: 'auto',
                    lineHeight: 1.8,
                    color: '#4B5563',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  }}
                >
                  Your enquiry has been submitted successfully. {formData.email && "We've sent a confirmation to your email."}
                  Our team will review your enquiry and contact you shortly.
                </Typography>
                
                {referenceNumber && (
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      p: 2,
                      maxWidth: '500px',
                      mx: 'auto',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: '#2E7D32',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                    }}
                  >
                    Reference Number: {referenceNumber}
                  </Typography>
                )}
                
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                  }}
                  onClick={handleStartOver}
                >
                  Make Another Enquiry
                </Button>
              </Paper>
            ) : (
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 3, sm: 4 },
                  borderRadius: 2,
                  bgcolor: 'white' 
                }}
              >
                <Box component="form" onSubmit={handleSubmit}>
                  {/* Enquiry Type Dropdown */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="enquiry-type-label">Enquiry Type</InputLabel>
                    <Select
                      labelId="enquiry-type-label"
                      id="enquiryType"
                      value={formData.enquiryType}
                      label="Enquiry Type"
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        enquiryType: e.target.value
                      }))}
                      required
                    >
                      {enquiryTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Name Field */}
                  <TextField
                    id="name"
                    label="Name"
                    fullWidth
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    sx={{ mb: 3 }}
                  />

                  {/* Email Field */}
                  <TextField
                    id="email"
                    label="Email"
                    type="email"
                    fullWidth
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    sx={{ mb: 3 }}
                  />

                  {/* Phone Field */}
                  <TextField
                    id="phone"
                    label="Phone"
                    fullWidth
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    sx={{ mb: 3 }}
                  />

                  {/* Message Field */}
                  <TextField
                    id="message"
                    label="Message"
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    sx={{ mb: 3 }}
                  />

                  {/* Show error if recaptcha has issues */}
                  {recaptchaError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {recaptchaError}
                    </Alert>
                  )}

                  {/* Only show recaptcha if no configuration error */}
                  {!recaptchaError && (
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                      <ReCAPTCHA
                        sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'} // Fallback to test key
                        onChange={handleCaptchaChange}
                      />
                    </Box>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={(!captchaValue && !import.meta.env.DEV) || isSubmitting} // Allow click in dev mode
                    sx={{
                      mt: 2,
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      bgcolor: '#0066b2',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#004c8c',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>

                  {/* Development mode indicator */}
                  {import.meta.env.DEV && (
                    <Typography 
                      variant="caption" 
                      align="center" 
                      sx={{ 
                        display: 'block',
                        mt: 1,
                        color: 'orange'
                      }}
                    >
                      Development mode: reCAPTCHA validation is bypassed
                    </Typography>
                  )}

                  <Typography 
                    variant="caption" 
                    align="center" 
                    sx={{ 
                      display: 'block',
                      mt: 3,
                      color: '#445668',
                      '& a': {
                        color: '#0066b2',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      },
                    }}
                  >
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    apply.
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </Container>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Enquiry; 